<template>
  <div v-if="location">
    <md-card>
      <!-- The title area-->
      <md-card-area md-inset>
        <md-card-header>
          <md-card-header-text>
            <div class="md-title">
              {{location.name}}
              <span>
                <md-progress-bar v-if="$apollo.loading" md-mode="query"/>
              </span>
            </div>
            <div class="md-subhead">
              <md-icon>location_on</md-icon>
              <span>{{location.address || "unknown address"}}</span>
            </div>
          </md-card-header-text>

          <!-- Location actions menu -->
          <md-menu v-if="isMine(location)" md-size="big" md-direction="bottom-end">
            <md-button class="md-icon-button" md-menu-trigger>
              <md-icon>more_vert</md-icon>
            </md-button>

            <md-menu-content>
              <md-menu-item @click="showEditLocationDialog = true">
                <span>Edit</span>
                <md-icon>edit</md-icon>
              </md-menu-item>

              <md-menu-item @click="showConfirmDelete = true">
                <span>Delete</span>
                <md-icon>delete</md-icon>
              </md-menu-item>
            </md-menu-content>
          </md-menu>
        </md-card-header>
        <md-card-content>{{location.description}}</md-card-content>

        <!-- Location actions -->
        <md-card-actions>
          <md-button class="md-icon-button md-primary" @click="newVenue">
            <md-icon>add_circle</md-icon>
            <md-tooltip>Add Venues</md-tooltip>
          </md-button>
          <md-button class="md-icon-button" @click="refetch">
            <md-icon>refresh</md-icon>
            <md-tooltip>Refetch location data</md-tooltip>
          </md-button>
        </md-card-actions>
      </md-card-area>

      <!-- The venues -->
      <md-card-content>
        <md-table v-model="location.venues" md-fixed-header>
          <md-table-toolbar>
            <div class="md-toolbar-section-start">
              <h1 class="md-title">Venues</h1>
            </div>
          </md-table-toolbar>

          <md-table-empty-state md-label="No venues at this location">
            <md-button v-if="currentUser" class="md-primary md-raised" @click="newVenue">Add Venues</md-button>
          </md-table-empty-state>

          <md-table-row slot="md-table-row" slot-scope="{ item }" md-selectable="single">
            <md-table-cell md-label="Name">{{ item.name }}</md-table-cell>
            <md-table-cell md-label="Description">{{ item.description }}</md-table-cell>
            <md-table-cell md-label="Reservation">{{ item.reservedBy }}</md-table-cell>
            <md-table-cell md-label="Actions">
              <md-button class="md-icon-button" @click="deleteVenue(item.id)">
                <md-icon>delete</md-icon>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>
    </md-card>

    <!-- Dialogs -->
    <AddVenueDialog
      :location="location"
      :show="showVenueDialog"
      @onClose="showVenueDialog = false"
    />
    <UpsertLocationDialog
      :show="showEditLocationDialog"
      @onClose="showEditLocationDialog = false"
      :location="location"
    />
    <md-dialog-confirm
      :md-active.sync="showConfirmDelete"
      md-title="Delete location?"
      md-content="Delete the location and all its venues"
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="deleteLocation"
    />
  </div>
  <div v-else>
    <md-progress-spinner class="wait" :md-diameter="100" :md-stroke="10" md-mode="indeterminate"/>
  </div>
</template>

<style lang="scss" scoped>
.md-card {
  width: 100%;
  margin: 0px !important;
  display: inline-block;
  vertical-align: top;
}
.wait {
  margin: 2em;
}
</style>

<script>
import gql from "graphql-tag";
import { LocationTools } from "@/js/helper";
import { locationQuery } from "@/graphql/location";
import EditField from "@/components/dialogs/editField.vue";
import AddVenueDialog from "@/components/dialogs/addVenueDialog.vue";
import UpsertLocationDialog from "@/components/dialogs/upsertLocationDialog.vue";

export default {
  name: "Location",
  components: { EditField, AddVenueDialog, UpsertLocationDialog },
  apollo: {
    location() {
      // this query has to be a function to be called after the component is created, giving access to the other properties
      return {
        query: locationQuery,
        variables() {
          return {
            id: this.locationId
          };
        }
      };
    }
  },

  data: function() {
    return {
      showEditLocationDialog: false,
      showVenueDialog: false,
      showConfirmDelete: false,
      name: {
        label: "Location Name",
        required: true
      },
      address: {
        label: "Address"
      },
      description: {
        label: "A Short description"
      },
      comments: {
        label: "Comments",
        multiline: true
      }
    };
  },

  computed: {
    inputs: {
      get() {
        return [this.name, this.address, this.description, this.comments];
      }
    },
    currentUser: {
      get() {
        return this.$store.state.currentUser;
      }
    },
    locationId: {
      get() {
        return this.$store.state.currentLocationId;
      }
    }
  },

  methods: {
    isMine: LocationTools.isMine,
    newVenue() {
      this.showVenueDialog = true;
    },
    deleteVenue(id) {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($locationId: ID!, $venueId: ID!) {
              deleteVenue(locationId: $locationId, venueId: $venueId)
            }
          `,
          variables: {
            locationId: this.location.id,
            venueId: id
          }
        })
        .then(result => {
          this.$root.$emit("updateLocation", this.location.id);
          this.$store.commit("setNotification", `Venue deleted`);
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to delete venue");
        });
    },
    refetch() {
      if (this.$apollo.queries.location)
        this.$apollo.queries.location.refetch();
    },
    deleteLocation() {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation($locationId: ID!) {
              deleteLocation(locationId: $locationId)
            }
          `,
          variables: {
            locationId: this.location.id
          }
        })
        .then(result => {
          this.$store.commit("setNotification", `Location has been deleted`);
          this.$router.push({ path: "/myLocations" });
        })
        .catch(err => {
          console.log(err);
          window.alert("Failed to delete location");
        });
    }
  },

  created() {
    // This is a temporary way of updating the location data (refetching on local change).
    // Ideally we subscribe to location-update events and refetch then, but firebase cloud functions
    // doesn't support web sockets for graphql subscriptions.
    // An alternative is to move the api off Firebase.
    // Another alternative is to do the subscription through Firebase events.
    this.$root.$on("updateLocation", id => {
      if (id === this.locationId) this.refetch();
    });
  }
};
</script>




// WEBPACK FOOTER //
// Location.vue?1e450164